/**
 * harness/useLongPress.js
 * T-Cron-2026-10-24 02:00 早间 (round-1-of-day) — 改进清单 #8「长按多选」
 *
 * 为什么需要独立 hook:
 *   「长按」这件事在移动端有一个天然的坑:**长按 = 系统的文本选择 /
 *   右键菜单 / 图片另存**。用户想在看板里长按多选,结果弹出了浏览器的
 *   「保存图片」菜单 —— 这个功能直接废了。
 *
 * 契约:
 *   · 450ms 触发(低于 iOS 的 500ms 系统长按阈值,保证我们的先响应);
 *   · 手指移动超过 10px 立即取消(用户在滚动列表 ⇒ 那是在滚动,不是长按);
 *   · 触发后给一次 12ms 短震动,确认「我收到你的长按了」(触感反馈 #3);
 *   · 触发后紧随其后的那次 click 会被 consumeLongPress() 吃掉 ——
 *     否则长按完松手会顺带打开详情抽屉,等于长按既进了多选又打开了详情;
 *   · 触摸在按钮(fav-btn / 版本切换)上时**不启动**长按计时 ——
 *     收藏按钮有自己的语义,不该顺手把卡片拉进多选态。
 *
 * 注意:本文件用 React,所以**必须显式 import React**(vite.config.js 的
 * esbuild.jsxInject 只对 .jsx 生效,.js 文件拿不到自动注入 —— 同
 * harness/useKeyboardShortcuts.js 的约定)。
 */
import React from 'react';

const DEFAULT_DELAY = 450;
const DEFAULT_MOVE_TOLERANCE = 10;

/**
 * @param {Object} opts
 * @param {() => void} opts.onLongPress 长按触发回调
 * @param {number} [opts.delay] 触发延时(ms)
 * @param {number} [opts.moveTolerance] 取消触发的移动阈值(px)
 */
export function useLongPress(opts = {}) {
  const { onLongPress, delay = DEFAULT_DELAY, moveTolerance = DEFAULT_MOVE_TOLERANCE } = opts;

  // 回调存 ref:定时器是异步触发的,直接闭包捕获会拿到旧回调。
  const cbRef = React.useRef(onLongPress);
  cbRef.current = onLongPress;

  const timerRef = React.useRef(null);
  const resetTimerRef = React.useRef(null);
  const originRef = React.useRef({ x: 0, y: 0 });
  const firedRef = React.useRef(false);
  const movedRef = React.useRef(false);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 组件卸载时清掉两个定时器,避免 setState 打在已卸载组件上。
  React.useEffect(() => () => {
    clearTimer();
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, [clearTimer]);

  const onTouchStart = React.useCallback((e) => {
    // 按钮上有自己的交互(收藏 / 版本切换),长按不走多选。
    if (e.target && e.target.closest && e.target.closest('button')) return;

    const t = e.touches && e.touches[0];
    if (!t) return;

    firedRef.current = false;
    movedRef.current = false;
    originRef.current = { x: t.clientX, y: t.clientY };

    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (movedRef.current) return;
      firedRef.current = true;
      // 兜底:万一这次长按后面没有 click(比如用户按住后移开了),
      // 1.2s 后把标记清掉,免得误吞掉后面某个无关的 click。
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => { firedRef.current = false; }, 1200);
      if (cbRef.current) cbRef.current();
    }, delay);
  }, [delay, clearTimer]);

  const onTouchMove = React.useCallback((e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - originRef.current.x);
    const dy = Math.abs(t.clientY - originRef.current.y);
    if (dx > moveTolerance || dy > moveTolerance) {
      // 用户在滚动 ⇒ 取消长按(否则滚动时手停一下就会误触发多选)
      movedRef.current = true;
      clearTimer();
    }
  }, [moveTolerance, clearTimer]);

  const onTouchEnd = React.useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  /**
   * 询问「刚才这次 click 是不是长按的后遗症」。
   * 返回 true ⇒ 调用方应当忽略这次 click。
   */
  const consumeLongPress = React.useCallback(() => {
    if (!firedRef.current) return false;
    firedRef.current = false;
    return true;
  }, []);

  /**
   * 只读查询「长按是否已经触发」(不消费)。
   *
   * 为什么需要:Android 的「长按 ⇒ 弹出系统菜单」阈值大约 500ms,
   * 而我们自己的长按定时器是 450ms。两者赛跑 —— 我们的定时器虽然先跑,
   * 但 React 的 setState → 重渲染是**异步**的,等到 DOM 上的
   * `selection-mode` 类名真正加上时,系统菜单可能已经弹出来了。
   *
   * ⇒ 组件层不能只看 React state,还要问这个 ref:
   *   `if (longPress.isFired() || selectionMode) e.preventDefault();`
   * 这是确定性的:不依赖重渲染时序。
   */
  const isFired = React.useCallback(() => firedRef.current, []);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
    consumeLongPress,
    isFired,
  };
}

export default useLongPress;
